import { AsyncPipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { NOTIFICATION_LIFETIME } from 'src/app/utils/global-constants';

import { BypassHtmlSecurityPipe } from '../pipes/bypass-html-security/bypass-html-security.pipe';
import { Nl2brPipe } from '../pipes/nl2br/nl2br.pipe';
import { StripHtmlPipe } from '../pipes/strip-html/strip-html.pipe';

import { Notification } from './notification';
import { NotificationService } from './notification.service';

export type NotificationPosition =
  | 'top-start'
  | 'top-center'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-center'
  | 'bottom-end';

@Component({
  selector: 'cc-notification',
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NgFor, NgTemplateOutlet, AsyncPipe, Nl2brPipe, BypassHtmlSecurityPipe, StripHtmlPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class NotificationComponent {
  @Input() public readonly position: NotificationPosition = 'bottom-end';

  @ViewChild('notificationContent', { static: false }) public readonly notificationContent!: TemplateRef<Element>;
  @ViewChild('notificationFooter', { static: false }) public readonly notificationFooter!: TemplateRef<Element>;

  public readonly notifications$: Observable<Notification[]> = this.notificationService.notifications$.pipe(
    map((notifications: Notification[]): Notification[] =>
      // Sometimes we can only specify strings and not pass in TemplateRef - for these cases we simply map the text
      // to our default TemplateRef and pass it into this default template
      notifications.map((notification: Notification): Notification => {
        if (typeof notification.content === 'string' || typeof notification.footer === 'string') {
          notification.context = this.updateNotificationContext(notification);
          notification.content = this.updateNotificationContent(notification);
          notification.footer = this.updateNotificationFooter(notification);
        }

        return notification;
      })
    )
  );

  public readonly defaultLifetime = NOTIFICATION_LIFETIME;

  public constructor(private readonly notificationService: NotificationService) {}

  public getNotificationLifetime(notification: Notification): number | null {
    return notification.autoDismiss ? (notification.lifetime ?? this.defaultLifetime) : null;
  }

  public close(notification: Notification): void {
    this.notificationService.clear(notification);
  }

  private updateNotificationContext(notification: Notification): Record<string, string | null> {
    return {
      // eslint-disable-next-line unicorn/no-useless-fallback-in-spread
      ...(notification.context ?? {}),
      renderContent: typeof notification.content === 'string' ? notification.content : null,
      renderFooter: typeof notification.footer === 'string' ? notification.footer : null
    };
  }

  private updateNotificationContent(notification: Notification): TemplateRef<Element> | string {
    return typeof notification.content === 'string' ? this.notificationContent : notification.content;
  }

  private updateNotificationFooter(notification: Notification): TemplateRef<Element> | string | undefined {
    return typeof notification.footer === 'string' ? this.notificationFooter : notification.footer;
  }
}
